import type { ReactNode } from 'react';
import { Chip } from '../../components/Chip';
import { AlertIcon, CheckIcon } from '../../components/icons';
import { BULK_ACTIONS, type BulkAction } from './bulkActions';

export type BulkItemStatus = 'done' | 'skipped' | 'failed';

export interface BulkResultItem {
  code: string;
  result: BulkItemStatus;
  /** Por qué se omitió o falló, en palabras: «tuvo escaneos: solo puede darse de baja». */
  motivo?: string | null;
  reason?: string | null;
  /** Dato del resultado: «en cuarentena hasta 15-09-2027». */
  detail?: string | null;
}

export interface BulkResultProps {
  action: BulkAction;
  items: BulkResultItem[];
  /** Sigue corriendo en segundo plano: la lista se completa sola. */
  running?: boolean;
  /** Códigos del lote mientras todavía no hay resultado por cada uno. */
  total?: number;
  /** Acceso a la auditoría del lote. */
  auditLink?: ReactNode;
}

function summary(action: BulkAction, items: BulkResultItem[]): string {
  const done = items.filter((i) => i.result === 'done').length;
  const skipped = items.filter((i) => i.result === 'skipped').length;
  const failed = items.filter((i) => i.result === 'failed').length;
  const spec = BULK_ACTIONS[action];
  const parts = [`${done} ${(done === 1 ? spec.doneLabel : spec.donePlural).toLowerCase()}`];
  if (skipped) parts.push(`${skipped} ${skipped === 1 ? 'omitido' : 'omitidos'}`);
  if (failed) parts.push(`${failed} ${failed === 1 ? 'fallido' : 'fallidos'}`);
  return parts.join(', ');
}

/** Resultado por código de una acción masiva (R3.4): hecho, omitido con motivo o fallido. Nunca un «listo» a secas. */
export function BulkResult({ action, items, running = false, total, auditLink }: BulkResultProps) {
  const spec = BULK_ACTIONS[action];
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-line bg-white p-4">
      <div role="status" className="flex flex-wrap items-center gap-2 rounded-md bg-green-soft px-3 py-2 text-sm text-success-text">
        <CheckIcon />
        <span className="font-semibold">{running ? `Procesando ${total ?? items.length} códigos…` : summary(action, items)}</span>
        {auditLink}
      </div>
      <h2 className="font-display text-base font-bold text-ink">Resultado por código</h2>
      <ul className="flex flex-col gap-1.5 text-sm" aria-live="polite">
        {items.map((item) => (
          <li key={item.code} className="flex flex-wrap items-center gap-2">
            <span className="font-mono font-semibold tracking-wider">{item.code}</span>
            {item.result === 'done' && (
              <Chip tone="success" dot={false}>
                <CheckIcon className="h-3.5 w-3.5" />
                {spec.doneLabel}
              </Chip>
            )}
            {item.result === 'skipped' && (
              <Chip tone="warning" dot={false}>
                <AlertIcon className="h-3.5 w-3.5" />
                Omitido
              </Chip>
            )}
            {item.result === 'failed' && (
              <Chip tone="error" dot={false}>
                <AlertIcon className="h-3.5 w-3.5" />
                Falló
              </Chip>
            )}
            <span className="text-ink-muted">{item.motivo ?? item.reason ?? item.detail ?? ''}</span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-ink-muted">La acción corre en segundo plano: puedes seguir trabajando y volver a este resultado.</p>
    </div>
  );
}
